import request from "@/lib/api/handler";

import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useProfileQuery } from "@/queries/user/profile";

import { QueryKeys } from "@/queries/utils";
import { CandidateNameLocationTitleFormSchema } from "@/components/forms/candidate/update-name-location-title/schema";
import { Candidate } from "@/queries/candidate/candidate";
import { IPerson } from "@/redux/features/candidateProfileSlice";
import { toast } from "sonner";
import { CandidateBiographyFormSchema } from "@/components/forms/candidate/update-biography/schema";
import { CandidateIdealEngagementFormSchema } from "@/components/forms/candidate/update-ideal-engagement/schema";
import { CandidateSkillFormSchema } from "@/components/forms/candidate/update-core-competencies/schema";
import { CandidateFundraisingFormSchema } from "@/components/forms/candidate/update-fundraising/schema";
import { CandidateAvailabilityFormSchema } from "@/components/forms/candidate/update-availability/schema";
import { CandidatePricingFormSchema } from "@/components/forms/candidate/update-pricing/schema";
import { CandidateLinksFormSchema } from "@/components/forms/candidate/update-links/schema";
import { CandidateQuestionnaireFormSchema } from "@/components/forms/candidate/update-questionnaire/schema";
import { CandidateFractionalExperienceFormSchema } from "@/components/forms/candidate/update-fractional-experience/schema";

import localDateToDateString from "@/utils/localDateToDateString";

type DataType = (
  | CandidateNameLocationTitleFormSchema
  | CandidateBiographyFormSchema
  | CandidateIdealEngagementFormSchema
  | CandidateSkillFormSchema
  | CandidateFundraisingFormSchema
  | CandidateAvailabilityFormSchema
  | CandidatePricingFormSchema
  | CandidateLinksFormSchema
  | CandidateQuestionnaireFormSchema
  | CandidateFractionalExperienceFormSchema
) & { emails: string[] };

export const useUpdateCandidateProfile = (
  id: string,
  onSuccess?: (v: IPerson) => void,
) => {
  const qc = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();
  const { profile } = useProfileQuery();

  const { mutate, isPending, error } = useMutation({
    mutationFn: async (data: DataType) => {
      const token = await getAccessTokenSilently();
      const { body, contentType } = buildBody(data);

      return request<IPerson>({
        method: "PATCH",
        path: `/api/candidate/${id}`,
        token,
        body,
        contentType,
      });
    },
    onSuccess: (data) => {
      const prevCandidate = qc.getQueryData<Candidate>(QueryKeys.candidate(id));
      if (prevCandidate) {
        const updatedCandidate = {
          ...prevCandidate,
          ...data,
        };

        qc.setQueryData<Candidate>(QueryKeys.candidate(id), updatedCandidate);
      }

      if (profile?.id === id) {
        const prevSelf = qc.getQueryData<Candidate>(QueryKeys.myCandidate);
        if (prevSelf) {
          const updatedCandidate = {
            ...prevSelf,
            ...data,
          };

          qc.setQueryData<Candidate>(QueryKeys.myCandidate, updatedCandidate);
        }
      }

      onSuccess?.(data);
    },
    onError: () => {
      toast.error("Failed to update profile, please try again later.");
    },
  });

  return {
    updateCandidateProfile: mutate,
    isUpdatingCandidateProfile: isPending,
    updateCandidateProfileError: error,
  };
};

function buildBody(data: DataType): {
  body: FormData | string;
  contentType: "application/x-www-form-urlencoded" | "application/json";
} {
  // Name, location, and headline section
  if ("name" in data || "resume" in data) {
    // Use FormData to send image
    const body = new FormData();

    addNonNullableField(body, "emails", data.emails);

    if ("name" in data) {
      addNonNullableField(body, "name", data.name);
      addNonNullableField(body, "titleAndEmployer", data.titleAndEmployer);
      addNonNullableField(body, "location", data.location);
      addNonNullableField(body, "yearsOfExperience", data.yearsOfExperience);
      addNonNullableField(
        body,
        "yearsOfHealthcareExperience",
        data.yearsOfHealthcareExperience,
      );
      addNonNullableField(body, "profilePicture", data.profilePicture);
      addNonNullableField(body, "isPublic", data.isPublic);
    } else if ("resume" in data) {
      addNonNullableField(body, "linkedIn", data.linkedIn);
      addNonNullableField(body, "personalWebsite", data.personalWebsite);
      addNonNullableField(body, "resume", data.resume);
    }

    return { body, contentType: "application/x-www-form-urlencoded" };
  }

  // If no file is being sent, just use JSON
  return {
    body: JSON.stringify({
      ...data,
      // Convert availabilityStartDate to a string
      ...("availabilityStartDate" in data && data.availabilityStartDate
        ? {
            availabilityStartDate: localDateToDateString(
              data.availabilityStartDate,
            ),
          }
        : {}),
    }),
    contentType: "application/json",
  };
}

function addNonNullableField(
  form: FormData,
  fieldName: string,
  value:
    | string
    | number
    | string[]
    | number[]
    | boolean
    | File
    | null
    | undefined,
) {
  if (value === undefined || value === null) {
    return;
  }

  if (Array.isArray(value)) {
    if (!value.length) {
      return form.append(`${fieldName}`, "[]");
    }

    return value.forEach((value) =>
      form.append(`${fieldName}[]`, value.toString()),
    );
  }

  if (typeof value === "number") {
    if (isNaN(value)) {
      return;
    }

    return form.append(fieldName, value.toString());
  }

  if (typeof value === "boolean") {
    return form.append(fieldName, value.toString());
  }

  form.append(fieldName, value);
}
